import { useMemo } from 'react';
import type { AskMessage, AskUser } from '@/components/common/Ask/Ask';
import type { DataRoomFile } from '@/types/documents/documents.types';
import type { TransformedUserWorkspace } from '@/utils/data/transformWorkSpaceItems';
import {
  ASSISTANT_COLOR,
  CURRENT_USER_COLOR,
  USER_COLOR_PALETTE,
} from './AskSession.constants';
import { detectMac, generateUserColor } from './AskSession.utils';

export type UseAskSessionComputedValuesParams = {
  messages: AskMessage[];
  searchQuery: string;
  pinnedMessageIds: Set<string>;

  currentUserDisplayName?: string;
  workspaceData: TransformedUserWorkspace | null;
  selectedDocuments: DataRoomFile[];
};

export type UseAskSessionComputedValuesResult = {
  filteredMessages: AskMessage[];
  searchMatches: string[];
  users: AskUser[];
  pinnedMessagesList: AskMessage[];
  isMac: boolean;
  documentNameToIdMap: Map<string, string>;
};

export const useAskSessionComputedValues = ({
  messages,
  searchQuery,
  pinnedMessageIds,
  currentUserDisplayName,
  workspaceData,
  selectedDocuments,
}: UseAskSessionComputedValuesParams): UseAskSessionComputedValuesResult => {
  const { filteredMessages, searchMatches } = useMemo(() => {
    if (!searchQuery.trim()) {
      return { filteredMessages: messages, searchMatches: [] as string[] };
    }

    const query = searchQuery.toLowerCase();
    const matches = messages
      .map((msg, index) => ({
        message: msg,
        index,
      }))
      .filter(({ message }) => message.content.toLowerCase().includes(query));

    return {
      filteredMessages: matches.map((m) => m.message),
      searchMatches: matches.map((m) => m.message.id),
    };
  }, [messages, searchQuery]);

  const users = useMemo(() => {
    const usersMap = new Map<string, AskUser>();

    // Add current user
    usersMap.set('currentUser', {
      id: 'currentUser',
      name: currentUserDisplayName || 'You',
      color: CURRENT_USER_COLOR,
    });

    // Add assistant
    usersMap.set('assistant', {
      id: 'assistant',
      name: 'Assistant',
      color: ASSISTANT_COLOR,
    });

    // Add users from messages
    messages.forEach((msg) => {
      if (msg.senderId !== 'currentUser' && msg.senderId !== 'assistant') {
        if (!usersMap.has(msg.senderId)) {
          usersMap.set(msg.senderId, {
            id: msg.senderId,
            name: msg.senderName,
            color: generateUserColor(msg.senderId, USER_COLOR_PALETTE),
          });
        }
      }
    });

    // Add workspace users
    if (workspaceData?.workspaceUsers?.userMetadata) {
      Object.entries(workspaceData.workspaceUsers.userMetadata).forEach(
        ([userId, metadata]) => {
          if (!usersMap.has(userId)) {
            usersMap.set(userId, {
              id: userId,
              name: metadata.userId || userId,
              color: generateUserColor(userId, USER_COLOR_PALETTE),
            });
          }
        },
      );
    }

    return Array.from(usersMap.values());
  }, [messages, workspaceData, currentUserDisplayName]);

  const pinnedMessagesList = useMemo(() => {
    return messages.filter((msg) => pinnedMessageIds.has(msg.id));
  }, [messages, pinnedMessageIds]);

  const isMac = useMemo(() => detectMac(), []);

  const documentNameToIdMap = useMemo(() => {
    const map = new Map<string, string>();

    const workspaceDocs =
      workspaceData?.dataRoom?.workspaceDataRoomFiles?.items?.filter(
        (item): item is DataRoomFile =>
          !!item &&
          typeof item === 'object' &&
          'dataRoomFileId' in item &&
          'name' in item &&
          !(item as DataRoomFile).markedForDeletion,
      ) || [];

    const allDocs = [...selectedDocuments, ...workspaceDocs];
    allDocs.forEach((doc) => {
      const name = (doc.name || '').trim();
      if (name) map.set(name, doc.dataRoomFileId);
    });

    return map;
  }, [workspaceData, selectedDocuments]);

  return {
    filteredMessages,
    searchMatches,
    users,
    pinnedMessagesList,
    isMac,
    documentNameToIdMap,
  };
};

